import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { ArrowsClockwise, CheckCircle, Warning } from "@phosphor-icons/react";

/**
 * LiveSyncButton — only visible to users in LIVE mode.
 * Triggers POST /api/meta/sync to pull last 30 days of real Meta data into MongoDB.
 * Shows last-sync timestamp + relative time.
 */
export default function LiveSyncButton({ onSynced }) {
  const { user } = useAuth();
  const [syncing, setSyncing] = useState(false);
  const [lastSync, setLastSync] = useState(null);
  const [msg, setMsg] = useState("");

  const isLive = user?.meta_mode_override === "live";

  useEffect(() => {
    if (!isLive) return;
    api.get("/meta/status").then((r) => {
      const ts = r.data?.last_sync?.synced_at;
      if (ts) setLastSync(ts);
    }).catch(() => {});
  }, [isLive]);

  if (!isLive) return null;

  const sync = async () => {
    setSyncing(true); setMsg("");
    try {
      const r = await api.post("/meta/sync");
      const res = r.data?.result || {};
      setLastSync(new Date().toISOString());
      const errCount = (res.errors || []).length;
      setMsg(errCount === 0 ? "Synced ✓" : `Partial (${errCount} errors)`);
      setTimeout(() => setMsg(""), 4000);
      onSynced?.(res);
    } catch (e) {
      setMsg("Sync failed");
      setTimeout(() => setMsg(""), 4000);
    } finally {
      setSyncing(false);
    }
  };

  const relativeTime = (iso) => {
    if (!iso) return "Never";
    const diff = Math.max(0, Date.now() - new Date(iso).getTime());
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  };

  return (
    <button
      data-testid="live-sync-button"
      onClick={sync}
      disabled={syncing}
      title={`Pull last 30 days from Meta. Last synced: ${relativeTime(lastSync)}`}
      className={`flex items-center gap-1.5 px-2.5 py-1.5 text-[11px] font-mono uppercase tracking-wider transition-colors border ${
        msg === "Synced ✓"
          ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-400"
          : msg.startsWith("Sync failed") || msg.startsWith("Partial")
          ? "border-amber-500/40 bg-amber-500/10 text-amber-400"
          : "border-orange-500/40 bg-orange-500/5 text-orange-400 hover:bg-orange-500/10"
      } disabled:opacity-50`}
    >
      {msg === "Synced ✓" ? (
        <CheckCircle size={12} weight="fill" />
      ) : msg.startsWith("Sync failed") || msg.startsWith("Partial") ? (
        <Warning size={12} weight="fill" />
      ) : (
        <ArrowsClockwise size={12} className={syncing ? "animate-spin" : ""} weight="bold" />
      )}
      <span>
        {syncing ? "Syncing..." : (msg || `Sync · ${relativeTime(lastSync)}`)}
      </span>
    </button>
  );
}
